/**
 * components/landing/home/CustomerJourney.jsx — Phase 2.3 · the
 * Customer Success Journey section.
 *
 * Answers the question:
 *   "What happens after I hit 'Subscribe'?"
 *
 * A 9-step guided walkthrough that reassures potential White-Label
 * customers that Kalki Ai Cloud will lead them from signup through
 * ongoing success without surprises.
 *
 * Layout
 * ──────
 *   Desktop / Tablet (md+):
 *     \u2022 Top progress bar with 9 clickable step markers (compact strip).
 *     \u2022 Large active-step card underneath the strip.
 *         - Left half : illustration for that step.
 *         - Right half: title, message, features, time estimate, CTAs.
 *     \u2022 Prev / Next controls + auto-play toggle.
 *     \u2022 Auto-advances every 6s; pauses on hover / keyboard focus /
 *       user click.
 *
 *   Mobile (<md):
 *     \u2022 Vertical timeline (dot rail on the left).
 *     \u2022 Each step is a stacked card with a compact illustration.
 *     \u2022 Progress line fills as the user scrolls through the section
 *       (IntersectionObserver on each step).
 *
 * Motion respects prefers-reduced-motion — the auto-play, fade
 * transitions and progress-line growth are all disabled when set.
 *
 * Deep-linking
 *   The section reads `#journey-<id>` on mount and updates the URL as
 *   the visitor changes step so demo/marketing can share links like
 *   `kalkiai.trade/#journey-domain`.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import Link from '@/shims/NextLink'
import {
  ChevronLeft, ChevronRight, Pause, Play, ArrowRight, Clock, Sparkles, Zap,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading, GhostButton } from '../design-system'
import { useInView } from './_shared'
import { JOURNEY_STEPS, TONE } from './journey/data'
import { ILLUSTRATIONS, STEP_ICONS } from './journey/illustrations'

// Auto-play tick (milliseconds).  Long enough to read; short enough to keep momentum.
const AUTOPLAY_MS = 6500

const CustomerJourney = () => {
  const ref     = useRef(null)
  const visible = useInView(ref, { rootMargin: '0px 0px -100px 0px' })

  const [activeNum,    setActiveNum]    = useState(1)
  const [playing,      setPlaying]      = useState(false)
  const [hovered,      setHovered]      = useState(false)
  const [reducedMotion, setReducedMotion] = useState(false)
  const stepEls = useRef({})            // id -> DOM node (mobile timeline)

  const active = useMemo(
    () => JOURNEY_STEPS.find((s) => s.num === activeNum) || JOURNEY_STEPS[0],
    [activeNum],
  )
  const tone = TONE[active.tone] || TONE.orange

  // Reduced motion watcher
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReducedMotion(mq.matches)
    const on = () => setReducedMotion(mq.matches)
    mq.addEventListener?.('change', on)
    return () => mq.removeEventListener?.('change', on)
  }, [])

  // Auto-play — turns on when the section first enters view (unless the
  // user has already interacted).  Pauses on hover.
  useEffect(() => {
    if (visible && !playing && !reducedMotion) setPlaying(true)
    // Only reacts to first-time visibility + reduced-motion — the
    // eslint dep-warning isn't relevant here.
  }, [visible, reducedMotion, playing])

  useEffect(() => {
    if (!playing || hovered || reducedMotion) return
    const t = setInterval(() => {
      setActiveNum((n) => (n >= JOURNEY_STEPS.length ? 1 : n + 1))
    }, AUTOPLAY_MS)
    return () => clearInterval(t)
  }, [playing, hovered, reducedMotion])

  // Deep-link support (inbound only) — a URL like /#journey-<id>
  // still lands the visitor on the right step when they arrive.  We
  // deliberately DO NOT write the hash back out as the auto-play
  // loop advances because that produces a confusing pattern where
  // the visible URL keeps mutating (e.g. kalkiai.trade/#journey-plan)
  // and users interpret it as a broken navigation redirect.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const m = (window.location.hash || '').match(/^#journey-(.+)$/)
    if (m) {
      const s = JOURNEY_STEPS.find((x) => x.id === m[1])
      if (s) { setActiveNum(s.num); setPlaying(false) }
    }
  }, [])

  const gotoNum = useCallback((n) => {
    const wrapped = ((n - 1 + JOURNEY_STEPS.length) % JOURNEY_STEPS.length) + 1
    setActiveNum(wrapped)
    setPlaying(false)     // any user-initiated jump pauses auto-play
  }, [])

  // Keyboard navigation while section has focus
  useEffect(() => {
    const onKey = (e) => {
      if (!ref.current || !ref.current.contains(document.activeElement)) return
      if (e.key === 'ArrowRight') { e.preventDefault(); gotoNum(activeNum + 1) }
      if (e.key === 'ArrowLeft')  { e.preventDefault(); gotoNum(activeNum - 1) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [activeNum, gotoNum])

  return (
    <section
      ref={ref}
      id="customer-journey"
      className={clsx(
        'relative overflow-hidden py-24 md:py-32 transition-opacity duration-700',
        visible ? 'opacity-100' : 'opacity-0 translate-y-4',
      )}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Ambient background */}
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.55]"
        style={{ background: `radial-gradient(60% 45% at 50% 20%, rgba(${tone.rgb},0.10), transparent 70%)` }} />
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />

      <Container size="wide" className="flex flex-col gap-10 md:gap-14">
        {/* ── Header ─────────────────────────────── */}
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Customer success journey</Eyebrow>
          <SectionHeading
            align="center"
            title={<>From plan to platform — in <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">one guided journey</span></>}
            subtitle="A structured, supported implementation — from selecting a subscription through going live under your own brand and beyond. Every stage is clear, automated where it can be, and backed by our team where it counts."
          />
        </div>

        {/* ══════════════════════════════════════════════════════════════
             DESKTOP + TABLET layout — horizontal stepper + active card
             ══════════════════════════════════════════════════════════════ */}
        <div className="hidden md:flex flex-col gap-8">
          <StepIndicator
            active={activeNum}
            onSelect={gotoNum}
            playing={playing && !hovered && !reducedMotion}
          />

          <ActiveStepCard step={active} tone={tone} reducedMotion={reducedMotion} />

          {/* Controls row */}
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => gotoNum(activeNum - 1)}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.03] px-3.5 py-2 text-xs font-semibold text-zinc-200 hover:text-white hover:bg-white/[0.06]"
              aria-label="Previous step"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Previous
            </button>
            <button
              onClick={() => setPlaying((p) => !p)}
              className={clsx(
                'inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-semibold transition-all',
                playing
                  ? 'border border-brand-gold/40 bg-brand-gold/10 text-brand-gold'
                  : 'border border-white/15 bg-white/[0.03] text-zinc-200 hover:text-white',
              )}
              aria-label={playing ? 'Pause auto-play' : 'Play journey'}
            >
              {playing ? <><Pause className="h-3.5 w-3.5" /> Pause</> : <><Play className="h-3.5 w-3.5" /> Auto-play</>}
            </button>
            <button
              onClick={() => gotoNum(activeNum + 1)}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.03] px-3.5 py-2 text-xs font-semibold text-zinc-200 hover:text-white hover:bg-white/[0.06]"
              aria-label="Next step"
            >
              Next <ChevronRight className="h-3.5 w-3.5" />
            </button>
            <span className="ml-2 text-[11px] font-mono text-zinc-500">
              {String(active.num).padStart(2, '0')} / {String(JOURNEY_STEPS.length).padStart(2, '0')}
            </span>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════
             MOBILE layout — vertical timeline with dot rail
             ══════════════════════════════════════════════════════════════ */}
        <div className="md:hidden">
          <MobileTimeline reducedMotion={reducedMotion} onEnter={(n) => setActiveNum(n)} />
        </div>

        {/* ── Closing CTA ────────────────────────── */}
        <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-8 backdrop-blur-xl flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-wider text-brand-gold font-semibold mb-1">Ready when you are.</div>
            <h4 className="text-xl md:text-2xl font-semibold text-white leading-tight">
              Launch your branded platform in under a week.
            </h4>
            <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
              Every step you see above is delivered end-to-end — our team walks with you from checkout to launch, and stays with you after.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <Link
              href="/pricing"
              className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-black transition-all hover:-translate-y-[1px]"
              style={{ background: 'linear-gradient(90deg, #FFC107, #FFE17A)', boxShadow: '0 10px 30px -10px rgba(255,193,7,0.55)' }}
            >
              View pricing <ArrowRight className="h-4 w-4" />
            </Link>
            <GhostButton href="/get-started" size="md">Schedule a demo</GhostButton>
          </div>
        </div>
      </Container>
    </section>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Top progress strip (desktop + tablet)
// ═══════════════════════════════════════════════════════════════════════
function StepIndicator({ active, onSelect, playing }) {
  return (
    <div className="relative">
      {/* rail line */}
      <div className="absolute left-6 right-6 top-[19px] h-px bg-white/10 -z-0" />
      {/* filled portion */}
      <div
        className="absolute left-6 top-[19px] h-px bg-gradient-to-r from-brand-gold to-[#FFE17A] -z-0 transition-all duration-500 ease-out"
        style={{ width: `calc((${(active - 1)} / ${JOURNEY_STEPS.length - 1}) * (100% - 3rem))` }}
      />

      {/* markers */}
      <ol className="relative z-10 flex items-start justify-between gap-1 md:gap-2">
        {JOURNEY_STEPS.map((s) => {
          const done   = s.num <  active
          const cur    = s.num === active
          const future = s.num >  active
          return (
            <li key={s.id} className="flex-1 flex flex-col items-center">
              <button
                onClick={() => onSelect(s.num)}
                aria-current={cur ? 'step' : undefined}
                aria-label={`Step ${s.num}: ${s.title}`}
                className={clsx(
                  'group relative flex h-10 w-10 items-center justify-center rounded-full border transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/60',
                  done
                    ? 'border-emerald-400/50 bg-emerald-400/15 text-emerald-300 hover:brightness-125'
                    : cur
                      ? 'border-brand-gold bg-gradient-to-br from-brand-gold to-[#FFE17A] text-black shadow-[0_10px_40px_-10px_rgba(255,193,7,0.7)]'
                      : 'border-white/15 bg-black/40 text-zinc-400 hover:text-white hover:border-white/30',
                )}
              >
                <span className="text-xs font-bold">{s.num}</span>
                {cur && playing && (
                  <span
                    className="absolute inset-0 rounded-full border border-brand-gold/60 animate-ping"
                    style={{ animationDuration: '2.4s' }}
                  />
                )}
              </button>
              <span className={clsx(
                'mt-2 text-[10.5px] font-semibold uppercase tracking-wider text-center hidden md:block',
                cur ? 'text-white' : future ? 'text-zinc-500' : 'text-emerald-300/80',
              )}>
                {s.short}
              </span>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Active step card (desktop + tablet)
// ═══════════════════════════════════════════════════════════════════════
function ActiveStepCard({ step, tone, reducedMotion }) {
  const HeadIcon    = STEP_ICONS[step.iconKey] || Sparkles
  const Illustration = ILLUSTRATIONS[step.illustration] || null

  // Key on step.id so React remounts the card, replaying the CSS fade.
  return (
    <div
      key={step.id}
      className={clsx(
        'relative rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] backdrop-blur-xl overflow-hidden',
        !reducedMotion && 'animate-brand-fade-up',
      )}
    >
      {/* Sub-glow tinted to the step's tone */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.65]"
        style={{ background: `radial-gradient(50% 40% at 10% 0%, rgba(${tone.rgb},0.12), transparent 70%)` }}
      />

      <div className="relative grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-8 p-6 md:p-8 lg:p-10">
        {/* Illustration side */}
        <div className="lg:col-span-2 order-2 lg:order-1">
          <div className="h-full min-h-[320px]">
            {Illustration && <Illustration tone={tone} />}
          </div>
        </div>

        {/* Text side */}
        <div className="lg:col-span-3 order-1 lg:order-2 flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <span
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border"
              style={{
                borderColor: `rgba(${tone.rgb},0.35)`,
                background: `rgba(${tone.rgb},0.08)`,
                color: tone.hex,
              }}
            >
              <HeadIcon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
            </span>
            <span
              className="inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.14em]"
              style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}
            >
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: tone.hex }} />
              Step {step.num} of {JOURNEY_STEPS.length}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.02] px-2.5 py-1 text-[10.5px] font-mono text-zinc-400">
              <Clock className="h-3 w-3" /> {step.estimatedTime}
            </span>
          </div>

          <h3 className="text-2xl md:text-3xl lg:text-[34px] font-semibold text-white tracking-tight leading-tight">
            {step.title}
          </h3>

          <blockquote
            className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-[15px] italic text-zinc-100 leading-relaxed"
            style={{ borderLeftColor: tone.hex, borderLeftWidth: 3 }}
          >
            “{step.message}”
          </blockquote>

          <p className="text-[14.5px] text-zinc-400 leading-relaxed">{step.summary}</p>

          {/* Features */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {step.features.map((f, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-zinc-200">
                <Zap className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: tone.hex }} />
                <span>{f}</span>
              </div>
            ))}
          </div>

          {/* Optional CTA */}
          {step.ctaHref && (
            <div className="pt-1">
              <Link
                href={step.ctaHref}
                className="inline-flex items-center gap-1.5 text-[13px] font-semibold hover:brightness-125"
                style={{ color: tone.hex }}
              >
                {step.ctaLabel || 'Learn more'} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Mobile vertical timeline
// ═══════════════════════════════════════════════════════════════════════
function MobileTimeline({ reducedMotion, onEnter }) {
  const rootRef = useRef(null)
  const [maxSeen, setMaxSeen] = useState(1)

  // Track which step is furthest into view — used to fill the timeline
  // rail and to notify the parent (for the top progress bar which is
  // hidden on mobile but still keeps the state consistent).
  useEffect(() => {
    const nodes = rootRef.current?.querySelectorAll('[data-step]')
    if (!nodes || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          const num = Number(e.target.getAttribute('data-step'))
          setMaxSeen((cur) => (num > cur ? num : cur))
          onEnter?.(num)
        }
      })
    }, { rootMargin: '-30% 0px -40% 0px' })
    nodes.forEach((n) => io.observe(n))
    return () => io.disconnect()
  }, [onEnter])

  return (
    <ol ref={rootRef} className="relative pl-8">
      {/* Rail (background) */}
      <div className="absolute left-3.5 top-2 bottom-2 w-px bg-white/10" />
      {/* Rail (filled) */}
      <div
        className={clsx(
          'absolute left-3.5 top-2 w-px bg-gradient-to-b from-brand-gold to-[#FFE17A]',
          !reducedMotion && 'transition-all duration-500 ease-out',
        )}
        style={{ height: `calc(${(maxSeen / JOURNEY_STEPS.length) * 100}% - 8px)` }}
      />

      {JOURNEY_STEPS.map((s) => {
        const HeadIcon    = STEP_ICONS[s.iconKey] || Sparkles
        const Illustration = ILLUSTRATIONS[s.illustration] || null
        const tone = TONE[s.tone] || TONE.orange
        const done = s.num < maxSeen
        const cur  = s.num === maxSeen
        return (
          <li key={s.id} data-step={s.num} className="relative mb-8 last:mb-0">
            {/* Rail dot */}
            <span
              className={clsx(
                'absolute -left-[22px] top-3 h-5 w-5 rounded-full border flex items-center justify-center',
                cur
                  ? 'border-brand-gold bg-gradient-to-br from-brand-gold to-[#FFE17A] text-black shadow-[0_10px_30px_-8px_rgba(255,193,7,0.7)]'
                  : done
                    ? 'border-emerald-400/50 bg-emerald-400/20 text-emerald-300'
                    : 'border-white/20 bg-black/60 text-zinc-500',
              )}
            >
              <span className="text-[10px] font-bold">{s.num}</span>
            </span>

            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 backdrop-blur-xl">
              <div className="flex items-center gap-2 mb-2">
                <HeadIcon className="h-4 w-4" style={{ color: tone.hex }} />
                <h4 className="text-base font-semibold text-white leading-tight">{s.title}</h4>
              </div>
              <blockquote
                className="rounded-lg border border-white/10 bg-white/[0.03] p-2.5 text-[13px] italic text-zinc-200 leading-relaxed mb-2.5"
                style={{ borderLeftColor: tone.hex, borderLeftWidth: 3 }}
              >
                “{s.message}”
              </blockquote>
              <p className="text-[13px] text-zinc-400 leading-relaxed mb-3">{s.summary}</p>
              {Illustration && (
                <div className="rounded-xl overflow-hidden h-[260px] mb-3">
                  <Illustration tone={tone} />
                </div>
              )}
              <div className="flex flex-wrap gap-1.5 mb-2">
                {s.features.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] px-2 py-0.5 text-[10.5px] text-zinc-300">
                    <Zap className="h-2.5 w-2.5" style={{ color: tone.hex }} /> {f}
                  </span>
                ))}
              </div>
              <div className="flex items-center gap-2 text-[10.5px] font-mono text-zinc-500">
                <Clock className="h-3 w-3" /> {s.estimatedTime}
              </div>
            </div>
          </li>
        )
      })}
    </ol>
  )
}

export default CustomerJourney
